import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, GraduationCap, ClipboardList, Lock, LogOut, UploadCloud, LayoutDashboard } from "lucide-react";
import { api, fmtDate, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LoginCard } from "./Login";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Empty = ({ text, to, cta }) => (
  <div className="lux-card p-10 text-center"><p className="text-white/60">{text}</p><Link to={to} className="btn-outline-gold mt-6">{cta}</Link></div>
);

const Row = ({ title, meta, status, testid }) => (
  <div className="lux-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={testid}>
    <div><p className="font-medium text-lg">{title}</p><p className="text-sm text-white/55 mt-1">{meta}</p></div>
    <span className="text-[11px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] w-fit">{status}</span>
  </div>
);

const SecureDocs = () => (
  <div className="lux-card p-10 md:p-14 text-center border-dashed border-[#D4AF37]/40" data-testid="secure-docs-placeholder">
    <UploadCloud className="w-14 h-14 text-[#D4AF37]/70 mx-auto" strokeWidth={1.2} />
    <h3 className="mt-6 text-3xl font-medium">Secure Document Upload</h3>
    <p className="mt-3 text-white/60 max-w-md mx-auto">Encrypted upload for IDs, income statements and tax documents is coming soon. Please don't email sensitive documents — our team will guide you.</p>
    <button disabled className="btn-gold mt-8" data-testid="secure-docs-upload-button"><Lock className="w-4 h-4" /> Coming Soon</button>
  </div>
);

const PortalBody = ({ user }) => {
  const { logout } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["portal"], queryFn: () => api.get("/portal/overview").then((r) => r.data) });
  const d = data || { appointments: [], registrations: [], intakes: [] };
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-12 pt-36 pb-24" data-testid="portal-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="eyebrow mb-4">Client Portal</p>
          <h1 className="text-4xl sm:text-5xl font-medium" data-testid="portal-welcome">Welcome, <span className="gold-text italic">{user.name?.split(" ")[0]}</span></h1>
          <p className="mt-3 text-sm text-white/50">{user.email}</p>
        </div>
        <div className="flex gap-3">
          {user.role === "admin" && <Link to="/admin" className="btn-outline-gold !py-3" data-testid="portal-admin-link"><LayoutDashboard className="w-4 h-4" /> Admin</Link>}
          <button onClick={logout} className="btn-glass !py-3" data-testid="portal-logout-button"><LogOut className="w-4 h-4" /> Sign Out</button>
        </div>
      </div>
      <Tabs defaultValue="appointments" className="mt-12">
        <TabsList className="bg-black/50 border border-white/10 h-auto p-1 flex-wrap rounded-full">
          {[["appointments", Calendar, "Appointments"], ["academy", GraduationCap, "Academy"], ["intake", ClipboardList, "Intake"], ["documents", Lock, "Documents"]].map(([v, I, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-full gap-2 px-5 py-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid={`portal-tab-${v}`}><I className="w-4 h-4" />{l}</TabsTrigger>
          ))}
        </TabsList>
        {isLoading ? <p className="mt-8 text-white/50">Loading…</p> : (
          <>
            <TabsContent value="appointments" className="mt-8 space-y-4">
              {d.appointments.length ? d.appointments.map((a) => <Row key={a.id} testid={`portal-appointment-${a.id}`} title={a.service} meta={`${fmtDate(a.date)} · ${fmtTime(a.time)} · ${a.meeting_type}`} status={a.status} />)
                : <Empty text="No appointments yet." to="/book" cta="Book a Consultation" />}
            </TabsContent>
            <TabsContent value="academy" className="mt-8 space-y-4">
              {d.registrations.length ? d.registrations.map((r) => <Row key={r.id} testid={`portal-registration-${r.id}`} title={r.event_title} meta={fmtDate(r.event_date)} status={r.status} />)
                : <Empty text="You haven't registered for any sessions." to="/academy" cta="Browse Academy" />}
            </TabsContent>
            <TabsContent value="intake" className="mt-8 space-y-4">
              {d.intakes.length ? d.intakes.map((i) => <Row key={i.id} testid={`portal-intake-${i.id}`} title={i.services.join(", ")} meta={`Submitted ${new Date(i.created_at).toLocaleDateString()}`} status={i.status} />)
                : <Empty text="No intake form on file." to="/get-started" cta="Start Intake" />}
            </TabsContent>
            <TabsContent value="documents" className="mt-8"><SecureDocs /></TabsContent>
          </>
        )}
      </Tabs>
      <p className="mt-10 text-xs text-white/40">Submissions are matched to your account by email address.</p>
    </section>
  );
};

export default function Portal() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen" />;
  if (!user) return <LoginCard />;
  return <PortalBody user={user} />;
}
